import React, { useState, useEffect } from "react";
import { X, Receipt, User, CreditCard, Calendar, Loader } from "lucide-react";
import { formatNumberWithSeparator } from "../utils/numberFormat";
import { getSalesOrderDetail } from "../api/salesService";

const PAYMENT_METHOD_BADGE = {
  Tunai: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Transfer: "bg-sky-50 text-sky-700 ring-sky-200",
  QRIS: "bg-violet-50 text-violet-700 ring-violet-200",
  Campuran: "bg-amber-50 text-amber-700 ring-amber-200",
  CASH: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function CustomerTransactionDetailModal({
  transaction,
  customerName,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    if (transaction?.id) {
      setLoading(true);
      getSalesOrderDetail(transaction.id)
        .then((res) => {
          setDetailData(res.data?.data || res.data || null);
        })
        .catch((err) => {
          console.error("Failed to fetch transaction details", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [transaction?.id]);

  if (!transaction) return null;

  const dataToDisplay = detailData || transaction;
  const items = Array.isArray(dataToDisplay.items) ? dataToDisplay.items : [];
  
  const subtotal =
    dataToDisplay.subtotal ??
    items.reduce((sum, item) => sum + Number(item.total_price ?? ((item.quantity || item.qty || 0) * (item.unit_price || item.price || 0))), 0);
  const discount = dataToDisplay.discount ?? dataToDisplay.discount_total ?? 0;
  const additionalCosts = dataToDisplay.additional_costs ?? dataToDisplay.additional_cost ?? 0;
  const total = dataToDisplay.grand_total ?? dataToDisplay.total ?? dataToDisplay.total_transaction ?? subtotal - discount + additionalCosts;
  const paymentMethod = dataToDisplay.payment_method || 
    (dataToDisplay.payments && dataToDisplay.payments.length > 0 ? dataToDisplay.payments.map(p => p.payment_method).join(', ') : "-");
  
  const methodBadge =
    PAYMENT_METHOD_BADGE[paymentMethod] ||
    "bg-slate-100 text-slate-700 ring-slate-200";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-xl shadow-2xl flex flex-col max-h-[92vh] modal-panel-enter overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 px-6 py-4 flex justify-between items-center shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white">Detail Transaksi</h2>
            <p className="text-teal-100 text-xs mt-0.5 font-mono truncate">
              {dataToDisplay.transaction_number || dataToDisplay.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/85 hover:text-white hover:bg-white/15 transition-colors flex-shrink-0"
            aria-label="Tutup">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto">
          {/* Meta info */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
              <Loader className="w-8 h-8 animate-spin text-teal-600 mb-2" />
              <p className="text-sm">Memuat detail transaksi...</p>
            </div>
          ) : (
            <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="flex items-start gap-3 bg-slate-50/70 ring-1 ring-slate-200 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Waktu Transaksi
                </p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {formatDateTime(dataToDisplay.created_at || dataToDisplay.order_date)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50/70 ring-1 ring-slate-200 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Metode Pembayaran
                </p>
                <span
                  className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${methodBadge}`}>
                  {paymentMethod}
                </span>
              </div>
            </div>
            {customerName && (
              <div className="flex items-start gap-3 bg-slate-50/70 ring-1 ring-slate-200 rounded-xl p-3">
                <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Pelanggan
                  </p>
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {dataToDisplay.customer_name || customerName}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3 bg-slate-50/70 ring-1 ring-slate-200 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Dibuat oleh
                </p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {dataToDisplay.created_by || dataToDisplay.cashier_name || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white ring-1 ring-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Daftar Item
              </p>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                Tidak ada rincian item
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-3 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 truncate">
                        {item.name || item.item_name}
                      </p>
                      {(item.asset_unit_name || (item.unit_name && !["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(item.unit_name).toLowerCase())) || (item.unit && typeof item.unit === "string" && !["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(item.unit).toLowerCase())) || item.unit?.name) && (
                        <p className="text-[11px] text-teal-700 mt-0.5 inline-flex items-center gap-1 bg-teal-50 ring-1 ring-teal-200 px-1.5 py-0.5 rounded font-medium">
                          Unit: {item.asset_unit_name || item.unit?.name || item.unit_name || item.unit}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-0.5">
                        {item.quantity || item.qty} {item.satuan || item.product_unit_name || item.satuan_name || (["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(item.unit_name || item.unit || '').toLowerCase()) ? (item.unit_name || item.unit) : "Pcs")} × Rp{" "}
                        {formatNumberWithSeparator(item.unit_price || item.price || 0)}
                      </p>
                      {Number(item.discount_amount || 0) > 0 && (
                        <p className="text-xs font-medium text-rose-600 mt-0.5">
                          Diskon Item -Rp {formatNumberWithSeparator(item.discount_amount)}
                        </p>
                      )}
                      {Number(item.additional_cost || 0) > 0 && (
                        <p className="text-xs font-medium text-blue-600 mt-0.5">
                          Biaya Tambahan Item +Rp {formatNumberWithSeparator(item.additional_cost)}
                        </p>
                      )}
                    </div>
                    <span className="font-semibold text-slate-900 whitespace-nowrap">
                      Rp{" "}
                      {formatNumberWithSeparator(
                        item.total_price ?? ((item.quantity || item.qty || 0) * (item.unit_price || item.price || 0)),
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="mt-4 bg-slate-50/70 ring-1 ring-slate-200 rounded-xl px-4 py-3 space-y-2">
            <div className="flex items-center justify-between text-sm text-slate-700">
              <span>Sub Total</span>
              <span className="font-medium">
                Rp {formatNumberWithSeparator(subtotal)}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between text-sm text-rose-600">
                <span>Diskon</span>
                <span className="font-medium">
                  - Rp {formatNumberWithSeparator(discount)}
                </span>
              </div>
            )}
            {additionalCosts > 0 && (
              <div className="flex items-center justify-between text-sm text-blue-600">
                <span>Biaya Tambahan</span>
                <span className="font-medium">
                  + Rp {formatNumberWithSeparator(additionalCosts)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span>Rp {formatNumberWithSeparator(total)}</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 mt-4 text-center">
            Informasi di atas merupakan rincian lengkap dari transaksi penjualan ini.
          </p>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-white flex items-center justify-end gap-2 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg ring-1 ring-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
